const DEFAULT_APP_TITLE = "智慧能源";
const MAX_TITLE_LENGTH = 100;
const HISTORY_UPDATE_DEBOUNCE_MS = 300;

/**
 * bn-portal-web 统一页签 Title 管理器
 *
 * 设计目标：
 * 1) document.title 只能由父应用写入，子应用禁止直接写 document.title；
 * 2) 子应用如果要改标题，只能调用 window.parent.microAppTitleManager.setMicroAppTitle；
 * 3) 父应用基于自身 router 路由表建立 path -> title 的映射，并在路由切换时自动应用默认标题；
 * 4) 为防止频繁调用导致的性能问题，对 history.replaceState 的 title 更新做 300ms 合并（leading + trailing）。
 */

/**
 * 领先防抖函数 (Leading Debounce)
 * 在 waitMs 时间内，第一次调用立即执行，后续调用会合并并在计时结束后执行最后一次。
 *
 * @param {Function} fn 要执行的函数
 * @param {number} waitMs 等待时间（毫秒）
 * @returns {Function} 防抖后的函数
 */
function leadingDebounce(fn, waitMs) {
  let timerId = null;
  let pendingArgs = null;

  return (...args) => {
    if (!timerId) {
      // 第一次调用立即执行
      fn(...args);
      timerId = window.setTimeout(() => {
        timerId = null;
        // 如果在等待期间有新的调用，执行最后一次
        if (pendingArgs) {
          const nextArgs = pendingArgs;
          pendingArgs = null;
          fn(...nextArgs);
        }
      }, waitMs);
      return;
    }

    // 在等待期间，记录最新的参数
    pendingArgs = args;
  };
}

/**
 * 安全地修剪字符串
 *
 * @param {any} value 输入值
 * @returns {string} 修剪后的字符串
 */
function safeTrimString(value) {
  if (typeof value !== "string") return "";
  return value.trim();
}

/**
 * title 入参校验与清洗：
 * - 非字符串直接判空
 * - trim 后为空字符串判空
 * - 超过 100 字符截断
 *
 * @param {string} title 原始标题
 * @returns {string} 清洗后的标题
 */
function sanitizeTitle(title) {
  const trimmed = safeTrimString(title);
  if (!trimmed) return "";
  return trimmed.length > MAX_TITLE_LENGTH ? trimmed.slice(0, MAX_TITLE_LENGTH) : trimmed;
}

/**
 * 标准化路径格式
 * 去掉末尾的斜杠，处理空路径。
 *
 * @param {string} path 原始路径
 * @returns {string} 标准化后的路径
 */
function normalizePath(path) {
  const value = safeTrimString(path);
  if (!value) return "";
  if (value === "/") return "/";
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

/**
 * 判断路径是否属于某个前缀段
 * 例如：/fullscreen/a/b 属于 /fullscreen 的前缀段。
 *
 * @param {string} fullPath 全路径
 * @param {string} prefixPath 前缀路径
 * @returns {boolean} 是否匹配
 */
function isSegmentPrefix(fullPath, prefixPath) {
  const full = normalizePath(fullPath);
  const prefix = normalizePath(prefixPath);
  if (!full || !prefix) return false;
  if (full === prefix) return true;
  if (prefix === "/") return true;
  if (!full.startsWith(prefix)) return false;
  // 确保是路径段匹配，而不是字符串部分匹配（如 /vpp-monitor 不应匹配 /vpp）
  const boundaryChar = full[prefix.length];
  return boundaryChar === "/";
}

/**
 * 使用 “最长前缀匹配” 查找 title：
 * - 支持嵌套路由：/fullscreen/a/b 会命中 /fullscreen
 * - 支持未来更细粒度的映射：如果 map 里同时存在 /vpp 与 /vpp/monitor，则优先命中更长的 /vpp/monitor
 *
 * @param {string} path 当前路径
 * @param {Map} map 路径到标题的映射表
 * @returns {string} 匹配到的标题
 */
function findBestTitleByPath(path, map) {
  const targetPath = normalizePath(path);
  if (!targetPath) return "";

  let bestTitle = "";
  let bestLen = -1;
  for (const [candidatePath, candidateTitle] of map.entries()) {
    if (isSegmentPrefix(targetPath, candidatePath) && candidatePath.length > bestLen) {
      bestTitle = candidateTitle;
      bestLen = candidatePath.length;
    }
  }
  return bestTitle;
}

/**
 * 更新浏览器历史记录中的标题
 *
 * @param {string} title 新标题
 */
function updateHistoryTitle(title) {
  try {
    // replaceState 不会触发页面刷新，仅更新历史记录条目中的标题
    // 保留现有的 history.state，防止破坏 vue-router 的内部状态导致回退报错
    window.history.replaceState(window.history.state, title, window.location.href);
  } catch {
    // 忽略可能的异常（如跨域安全限制等）
  }
}

/**
 * 判断当前路径是否属于微应用
 *
 * @param {string} path 当前路径
 * @returns {boolean} 是否为微应用路径
 */
function isMicroAppPath(path) {
  const p = normalizePath(path);
  return (
    p === "/vpp" ||
    p.startsWith("/vpp/") ||
    p === "/ops" ||
    p.startsWith("/ops/") ||
    p === "/uav" ||
    p.startsWith("/uav/")
  );
}

/**
 * 初始化 Title 管理器（建议在 main.js 启动阶段 await）
 *
 * @param {object} options
 * @param {import('vue-router').Router} options.router Vue Router 实例
 * @param {string} options.defaultTitle 系统默认标题（兜底）
 *
 * 行为说明：
 * - 会把 { setMicroAppTitle } 挂载到 window.microAppTitleManager
 * - 会注册 router.afterEach，在路由切换时设置默认标题（微应用路由段会“让位”给子应用请求的 title）
 */
export async function initMicroAppTitleManager({ router, defaultTitle = DEFAULT_APP_TITLE } = {}) {
  // 1. 初始化默认标题
  const initialDefaultTitle = sanitizeTitle(defaultTitle) || DEFAULT_APP_TITLE;

  // 2. 内部状态管理
  const routeTitleMap = new Map(); // 存储 path -> title 的映射
  let currentRouteDefaultTitle = initialDefaultTitle; // 当前路由对应的默认标题
  let microOverrideActive = false; // 标记当前标题是否已被子应用主动覆盖

  /**
   * 立即更新 document.title，history.title 通过 300ms 防抖更新：
   * - document.title 更新要满足 < 100ms 的响应要求，因此不延迟
   * - history.replaceState 对性能敏感，采用 leading + trailing 的方式合并高频调用
   */
  const debouncedHistoryUpdate = leadingDebounce(updateHistoryTitle, HISTORY_UPDATE_DEBOUNCE_MS);

  /**
   * 应用文档标题的核心函数
   *
   * @param {string} title 要设置的标题
   */
  const applyDocumentTitle = (title) => {
    const finalTitle = sanitizeTitle(title);
    if (!finalTitle) return;
    if (typeof document !== "undefined") {
      document.title = finalTitle;
    }
    // 同步更新 history.title（带防抖）
    debouncedHistoryUpdate(finalTitle);
  };

  /**
   * 根据路径设置该路由段的默认标题
   *
   * @param {string} path 当前路由路径
   */
  const setRouteDefaultByPath = (path) => {
    const best = findBestTitleByPath(path, routeTitleMap);
    currentRouteDefaultTitle = best || initialDefaultTitle;

    // 如果是微应用路径
    if (isMicroAppPath(path)) {
      // 只有在子应用没有主动覆盖标题的情况下，才应用父应用定义的默认标题
      if (!microOverrideActive) applyDocumentTitle(currentRouteDefaultTitle);
      return;
    }

    // 如果切换回普通页面，重置覆盖标记并应用标题
    microOverrideActive = false;
    applyDocumentTitle(currentRouteDefaultTitle);
  };

  /**
   * 暴露给子应用的 API：设置微应用标题
   *
   * @param {string} title 子应用请求设置的新标题
   */
  const setMicroAppTitle = (title) => {
    const finalTitle = sanitizeTitle(title);
    if (!finalTitle) return;
    // 标记子应用已接管标题控制权
    microOverrideActive = true;
    applyDocumentTitle(finalTitle);
  };

  /**
   * 将 API 挂载到全局 window 对象，供子应用通过 window.parent 调用
   */
  const exposeToWindow = () => {
    if (typeof window === "undefined") return;
    const api = { setMicroAppTitle };
    Object.defineProperty(window, "microAppTitleManager", {
      configurable: true,
      writable: false,
      enumerable: true,
      value: api,
    });
  };

  // 3. 执行初始化逻辑
  exposeToWindow();
  applyDocumentTitle(initialDefaultTitle);

  // 4. 预置常用路径的标题映射
  [
    ["/home", "首页"],
    ["/login", "登录"],
    ["/forget", "忘记密码"],
    ["/vpp", "虚拟电厂平台"],
    ["/ops", "运维平台"],
    ["/uav", "无人机平台"],
  ].forEach(([path, title]) => {
    if (!routeTitleMap.has(path)) {
      routeTitleMap.set(path, title);
    }
  });

  // 5. 从 Vue Router 路由表中自动提取标题配置
  if (router?.getRoutes) {
    router.getRoutes().forEach((r) => {
      const rawPath = safeTrimString(r.path);
      // 过滤动态路由（带冒号的）
      if (!rawPath || rawPath.includes(":")) return;
      const title = safeTrimString(r.meta?.title) || safeTrimString(r.name);
      if (!title) return;
      const normalized = normalizePath(rawPath);
      // 避免覆盖已有的手动配置
      if (!routeTitleMap.has(normalized)) {
        routeTitleMap.set(normalized, title);
      }
    });
  }

  // 6. 注册路由守卫，实现标题自动切换
  if (router) {
    const current = router.currentRoute?.value;
    if (current?.path) setRouteDefaultByPath(current.path);
    router.afterEach((to) => {
      setRouteDefaultByPath(to.path);
    });
  }

  // 7. 返回内部状态和方法（供父应用内部可能的特殊需求使用）
  return {
    setMicroAppTitle,
    getRouteTitleMap: () => routeTitleMap,
    getCurrentRouteDefaultTitle: () => currentRouteDefaultTitle,
  };
}
